import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './TopGainers.css';

const TopGainers = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gainers, setGainers] = useState([]);
  const [periodDays, setPeriodDays] = useState(15);
  const [useRealtime, setUseRealtime] = useState(false);
  const [dataSource, setDataSource] = useState('precalculated');

  const { token, logout } = useAuth();

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        days: periodDays.toString(),
        limit: '100',
        realtime: useRealtime ? '1' : '0'
      });

      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`/api/alpaca/top-gainers?${params.toString()}`, { headers });

      if (response.status === 401) {
        let msg = 'Token invalide ou expiré';
        try {
          const j = await response.json();
          msg = j.message || msg;
        } catch {}
        setError(msg);
        await logout();
        return;
      }

      if (!response.ok) {
        let msg = 'Erreur lors du chargement des top gainers';
        try {
          const j = await response.json();
          msg = j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      if (data.success) {
        setGainers(data.data || []);
        setDataSource(data.source || 'precalculated');
        // Utiliser la période retournée par l'API
        if (data.period_days) {
          setPeriodDays(data.period_days);
        }
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [periodDays, useRealtime]);

  const formatPrice = (price) => {
    return typeof price === 'number' ? price.toFixed(2) : '-';
  };

  const formatPercent = (percent) => {
    if (typeof percent !== 'number') return '-';
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('fr-FR');
    } catch {
      return dateStr;
    }
  };

  const getPercentColor = (percent) => {
    if (typeof percent !== 'number') return '';
    return percent >= 0 ? 'positive' : 'negative';
  };

  return (
    <div className="top-gainers-page">
      <div className="top-gainers-header">
        <h1 className="top-gainers-title">🚀 Top Gainers</h1>
        <p className="top-gainers-subtitle">Les 100 actions avec la plus forte augmentation sur les 15 derniers jours</p>
      </div>

      {/* Filtres */}
      <div className="top-gainers-filters">
        <div className="filter-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="use-realtime"
              checked={useRealtime}
              onChange={(e) => setUseRealtime(e.target.checked)}
            />
            <label htmlFor="use-realtime" style={{ cursor: 'pointer' }}>
              Calcul en temps réel
            </label>
          </div>
          {useRealtime && (
            <div>
              <label htmlFor="period-days">Période (jours) :</label>
              <select
                id="period-days"
                className="filter-select"
                value={periodDays}
                onChange={(e) => setPeriodDays(parseInt(e.target.value))}
                style={{ marginLeft: '0.5rem' }}
              >
                <option value="7">7 jours</option>
                <option value="15">15 jours</option>
                <option value="30">30 jours</option>
                <option value="60">60 jours</option>
                <option value="90">90 jours</option>
              </select>
            </div>
          )}
        </div>
        {!useRealtime && (
          <div style={{ 
            marginTop: '0.5rem', 
            padding: '0.75rem', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#1976d2'
          }}>
            ℹ️ Les données sont pré-calculées sur <strong>15 jours</strong> et mises à jour quotidiennement à 09:00.
            {dataSource === 'precalculated' && ' (Source: table pré-calculée)'}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des top gainers...</p>
        </div>
      ) : (
        <>
          <div className="top-gainers-stats">
            <p>
              <strong>{gainers.length}</strong> actions trouvées 
              {useRealtime ? (
                <> sur les <strong>{periodDays}</strong> derniers jours (calcul en temps réel)</>
              ) : (
                <> sur les <strong>{periodDays}</strong> derniers jours (données pré-calculées)</>
              )}
            </p>
          </div>

          {gainers.length === 0 ? (
            <div className="empty-message">
              <p>Aucun gainer trouvé.</p>
            </div>
          ) : (
            <div className="top-gainers-table-container">
              <table className="top-gainers-table">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Symbole</th>
                    <th>Prix actuel</th>
                    <th>Date actuelle</th>
                    <th>Prix ancien</th>
                    <th>Date ancienne</th>
                    <th>Variation</th>
                  </tr>
                </thead>
                <tbody>
                  {gainers.map((gainer, index) => (
                    <tr key={gainer.symbol || index}>
                      <td className="rank-cell">{index + 1}</td>
                      <td><strong>{gainer.symbol}</strong></td>
                      <td className="price-cell">${formatPrice(gainer.current_price)}</td>
                      <td>{formatDate(gainer.current_date)}</td>
                      <td className="price-cell">${formatPrice(gainer.old_price)}</td>
                      <td>{formatDate(gainer.old_date)}</td>
                      <td className={`percent-cell ${getPercentColor(gainer.change_percent)}`}>
                        {formatPercent(gainer.change_percent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopGainers;




